import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import { ar, enUS } from "date-fns/locale";
import { Bell, ChevronRight } from "lucide-react";
import { useLocation } from "wouter";
import {
  useNotificationHistory,
  useMarkAsRead,
  useMarkAllAsRead,
  type Notification,
} from "@/hooks/use-notifications";
import { Button } from "@/components/ui/button";
import { CheckCheck } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";

function NotificationCard({
  notification,
  isRtl,
  onRead,
  isMarkingRead,
}: {
  notification: Notification;
  isRtl: boolean;
  onRead: (id: string) => void;
  isMarkingRead: boolean;
}) {
  const { t } = useTranslation();
  const [, navigate] = useLocation();
  const title = isRtl
    ? notification.titleAr || notification.titleEn
    : notification.titleEn || notification.titleAr;
  const body = isRtl
    ? notification.bodyAr || notification.bodyEn
    : notification.bodyEn || notification.bodyAr;
  const dateValue = notification.sentAt || notification.createdAt;
  const orderId = notification.data?.orderId;

  const handleClick = () => {
    if (isMarkingRead) return;
    if (!notification.isRead) onRead(notification.id);
    if (orderId) navigate(`/orders/${orderId}?from=notification`);
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={(notification.isRead && !orderId) || isMarkingRead}
      className="w-full text-start bg-card rounded-xl border border-border/50 p-4 transition-colors hover:bg-muted/40 data-[unread=true]:border-primary/40"
      data-unread={!notification.isRead}
      data-testid={`notification-card-${notification.id}`}
    >
      <div className="flex items-start gap-3">
        {!notification.isRead && (
          <span
            className="mt-1.5 h-2.5 w-2.5 rounded-full bg-blue-500 flex-shrink-0"
            aria-label={t("notifications.markAsRead")}
          />
        )}
        <div className="flex-1 min-w-0">
          <p className={`text-sm ${notification.isRead ? "font-medium" : "font-semibold"}`}>
            {title}
          </p>
          {body && (
            <p className="text-sm text-muted-foreground mt-1 whitespace-pre-line">{body}</p>
          )}
          {dateValue && (
            <p className="text-xs text-muted-foreground mt-1.5">
              {format(new Date(dateValue), "PPP", { locale: isRtl ? ar : enUS })}
            </p>
          )}
        </div>
        {orderId && (
          <ChevronRight className={`h-4 w-4 text-muted-foreground flex-shrink-0 mt-1 ${isRtl ? "rotate-180" : ""}`} />
        )}
      </div>
    </button>
  );
}

export default function Notifications() {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language === "ar";

  const { data: notifications, isLoading } = useNotificationHistory(1, 20);
  const markAsRead = useMarkAsRead();
  const markAllAsRead = useMarkAllAsRead();

  const list = notifications ?? [];
  const hasUnread = list.some((n) => !n.isRead);

  return (
    <div className="min-h-screen pt-24 pb-24 bg-background" dir={isRtl ? "rtl" : "ltr"}>
      <div className="container mx-auto px-4 max-w-2xl">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Bell className="w-6 h-6 text-primary" />
            {t("notifications.title")}
          </h1>
          {hasUnread && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => markAllAsRead.mutate()}
              disabled={markAllAsRead.isPending}
              data-testid="button-mark-all-read"
            >
              <CheckCheck className="w-4 h-4 me-2" />
              {t("notifications.markAllRead")}
            </Button>
          )}
        </div>

        {isLoading ? (
          <div className="space-y-3" data-testid="notifications-loading">
            {Array.from({ length: 4 }).map((_, i) => (
              <div
                key={i}
                className="bg-card rounded-xl border border-border/50 p-4 flex items-start gap-3"
              >
                <Skeleton className="h-2.5 w-2.5 rounded-full mt-1.5" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-4 w-2/3" />
                  <Skeleton className="h-3 w-full" />
                  <Skeleton className="h-3 w-1/3" />
                </div>
              </div>
            ))}
          </div>
        ) : list.length === 0 ? (
          <div className="text-center py-12 bg-card rounded-2xl border border-border/50">
            <Bell className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">{t("notifications.noNotifications")}</p>
          </div>
        ) : (
          <div className="space-y-2">
            {list.map((notification) => (
              <NotificationCard
                key={notification.id}
                notification={notification}
                isRtl={isRtl}
                onRead={(id) => markAsRead.mutate(id)}
                isMarkingRead={
                  markAsRead.isPending && markAsRead.variables === notification.id
                }
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
